import { Injectable, computed, signal } from '@angular/core';
import User, { UserRole } from '../models/user';

const AUTH_STORAGE_KEY = 'character-codex-auth-user';

export type ActiveUserSnapshot = {
  id: string;
  name: string;
  roles: UserRole[];
  currentRole: UserRole;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  authenticatedUser = signal<User | null>(null);
  currentUser = signal<ActiveUserSnapshot | null>(null);
  isLoggedIn = computed(() => this.authenticatedUser() !== null || this.currentUser() !== null);
  currentRole = computed(() => this.currentUser()?.currentRole ?? null);
  hasSelectedRole = computed(() => this.currentUser() !== null);
  availableRoles = computed(() => {
    if (this.authenticatedUser()) {
      return this.authenticatedUser()!.roles;
    }

    return this.currentUser()?.roles ?? [];
  });

  login(user: User): void {
    const safeUser = new User({
      id: user.id,
      name: user.name,
      username: user.username,
      email: user.email,
      roles: user.roles,
    });

    this.authenticatedUser.set(safeUser);
    this.currentUser.set(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }

  enterAs(role: UserRole): void {
    const availableRoles = this.availableRoles();
    const activeUser = this.authenticatedUser() ?? this.currentUser();

    if (!activeUser || !availableRoles.includes(role)) {
      return;
    }

    const snapshot: ActiveUserSnapshot = {
      id: activeUser.id,
      name: activeUser.name,
      roles: availableRoles,
      currentRole: role,
    };

    this.currentUser.set(snapshot);
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(snapshot));
  }

  logout(): void {
    this.authenticatedUser.set(null);
    this.currentUser.set(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }

  hasRole(role: UserRole): boolean {
    return this.currentRole() === role;
  }

  restoreSession(): void {
    const rawUser = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!rawUser) {
      return;
    }

    try {
      const parsedUser = JSON.parse(rawUser) as ActiveUserSnapshot;

      if (
        !parsedUser?.id ||
        !parsedUser?.name ||
        !parsedUser?.currentRole ||
        !Array.isArray(parsedUser.roles)
      ) {
        throw new Error('Invalid stored auth snapshot.');
      }

      this.currentUser.set(parsedUser);
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      this.currentUser.set(null);
    }
  }
}
